import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import TechnicianCard from './components/TechnicianCard';
import UnassignedOrderCard from './components/UnassignedOrderCard';
import AssignmentSummary from './components/AssignmentSummary';
import FilterPanel from './components/FilterPanel';
import BulkAssignmentModal from './components/BulkAssignmentModal';
import AddTechnicianModal from './components/AddTechnicianModal';
import TechnicianDetailsModal from './components/TechnicianDetailsModal';
import useSupabase from '../../hooks/useSupabase';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

const TechnicianAssignment = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { technicians, orders } = useSupabase();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [filters, setFilters] = useState({});
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [isAddTechnicianModalOpen, setIsAddTechnicianModalOpen] = useState(false);
  const [isTechnicianDetailsModalOpen, setIsTechnicianDetailsModalOpen] = useState(false);
  const [selectedTechnician, setSelectedTechnician] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('priority');
  const [draggedOrder, setDraggedOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [techniciansList, setTechniciansList] = useState([]);
  const [availableOrders, setAvailableOrders] = useState([]);

  // Load data from database
  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load technicians from the new technicians table
      
      if (!technicians?.getAll) {
        throw new Error('technicians.getAll method is not available');
      }

      const { data: techniciansData, error: techniciansError } = await technicians?.getAll({ 
        status: 'active'
      });

      if (techniciansError) {
        console.error('Technicians error:', techniciansError);
        
        // Set empty array and show error message
        setTechniciansList([]);
        setError(`Fehler beim Laden der Techniker: ${techniciansError.message}`);
        return;
      }

      // Transform technicians data to match component expectations
      const transformedTechnicians = techniciansData?.map(tech => ({
        id: tech.id,
        name: `${tech.first_name} ${tech.last_name}`,
        employeeId: tech.id?.substring(0, 8),
        status: tech.status || 'active', // Use actual status from database
        currentOrders: 0, // Will be calculated after loading orders
        completedToday: 0, // Will be calculated after loading orders
        skills: [], // No skills field in technicians table
        location: 'Köln', // Default location
        distance: '0 km', // This would need to be calculated
        phone: tech.phone || '',
        email: tech.email || ''
      })) || [];


      // If no technicians found, show a message but don't set empty array
      if (!transformedTechnicians || transformedTechnicians.length === 0) {
      }

      setTechniciansList(transformedTechnicians);

      // Load all orders to show assignment status
      const { data: ordersData, error: ordersError } = await orders?.getAll();

      if (ordersError) {
        throw ordersError;
      }

      // Transform orders data to match component expectations
      const transformedOrders = ordersData?.map(order => ({
        id: order.id,
        orderNumber: order.order_number,
        vin: order.vin || 'N/A',
        vehicleMake: 'Unbekannt', // No vehicle join in new structure
        vehicleModel: 'Unbekannt', // No vehicle join in new structure
        vehicleYear: 'Unbekannt', // No vehicle join in new structure
        dealership: typeof order.car_house === 'object' ? order.car_house?.name || 'Unbekannt' : 'Unbekannt',
        serviceType: typeof order.service_type === 'object' ? order.service_type?.display_name || 'Unbekannt' : order.service_type || 'Unbekannt',
        serviceDescription: order.description || 'Keine Beschreibung',
        priority: order.priority,
        estimatedDuration: '1-2 Stunden', // Default - you might want to add this field
        requiredSkills: [], // This would need to be determined based on service type
        createdAt: new Date(order.created_at),
        slaDeadline: new Date(order.created_at), // Default - you might want to add SLA field
        customerName: order.customer_name || 'Unbekannt',
        customerPhone: order.customer_phone || '',
        assignedTechnician: order.assigned_technician ? {
          id: order.assigned_technician.id,
          name: typeof order.assigned_technician === 'object' ? 
            (order.assigned_technician.full_name || order.assigned_technician.first_name + ' ' + order.assigned_technician.last_name || 'Unbekannter Technician') : 
            'Unbekannter Technician'
        } : null,
        status: order.status,
        actualCompletion: order.actual_completion,
        updatedAt: order.updated_at
      })) || [];

      // Calculate current orders and completed orders today for each technician
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Start of today
      
      const updatedTechnicians = transformedTechnicians.map(tech => {
        // Count current orders (assigned but not completed)
        const currentOrders = transformedOrders.filter(order => 
          order.assignedTechnician?.id === tech.id && 
          order.status !== 'completed' && 
          order.status !== 'cancelled'
        ).length;
        
        // Count completed orders today
        const completedToday = transformedOrders.filter(order => 
          order.assignedTechnician?.id === tech.id && 
          order.status === 'completed' && 
          new Date(order.actualCompletion || order.updatedAt) >= today
        ).length;
        
        return {
          ...tech,
          currentOrders,
          completedToday
        };
      });
      
      
      setTechniciansList(updatedTechnicians);
      
      // Filter orders to only show orders available for assignment
      // Show orders with status 'pending' (Neu) or 'review' (In Prüfung) that need technician assignment
      const availableOrders = transformedOrders.filter(order => 
        !order.assignedTechnician && 
        order.status !== 'completed' && 
        order.status !== 'cancelled' &&
        order.status !== 'assigned' &&
        order.status !== 'in_progress' &&
        (order.status === 'pending' || order.status === 'review') // Show both pending and review orders
      );
      
      setAvailableOrders(availableOrders);

    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Function to refresh technician data (order counts)
  const refreshTechnicianData = async () => {
    try {
      // Get updated orders data
      const { data: ordersData, error: ordersError } = await orders?.getAll();
      if (ordersError) throw ordersError;

      const transformedOrders = ordersData?.map(order => ({
        id: order.id,
        orderNumber: order.order_number,
        vin: order.vin || 'N/A',
        vehicleMake: 'Unbekannt', // No vehicle join in new structure
        vehicleModel: 'Unbekannt', // No vehicle join in new structure
        vehicleYear: 'Unbekannt', // No vehicle join in new structure
        dealership: typeof order.car_house === 'object' ? order.car_house?.name || 'Unbekannt' : 'Unbekannt',
        serviceType: typeof order.service_type === 'object' ? order.service_type?.display_name || 'Unbekannt' : order.service_type || 'Unbekannt',
        serviceDescription: order.description || 'Keine Beschreibung',
        priority: order.priority,
        estimatedDuration: '1-2 Stunden',
        requiredSkills: [],
        createdAt: new Date(order.created_at),
        slaDeadline: new Date(order.created_at),
        customerName: order.customer_name || 'Unbekannt',
        customerPhone: order.customer_phone || '',
        assignedTechnician: order.assigned_technician ? {
          id: order.assigned_technician.id,
          name: typeof order.assigned_technician === 'object' ? 
            (order.assigned_technician.full_name || order.assigned_technician.first_name + ' ' + order.assigned_technician.last_name || 'Unbekannter Techniker') : 
            'Unbekannter Techniker'
        } : null,
        status: order.status,
        actualCompletion: order.actual_completion,
        updatedAt: order.updated_at
      })) || [];

      // Update technician order counts
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const updatedTechnicians = techniciansList.map(tech => {
        const currentOrders = transformedOrders.filter(order => 
          order.assignedTechnician?.id === tech.id && 
          order.status !== 'completed' && 
          order.status !== 'cancelled'
        ).length;
        
        const completedToday = transformedOrders.filter(order => 
          order.assignedTechnician?.id === tech.id && 
          order.status === 'completed' && 
          new Date(order.actualCompletion || order.updatedAt) >= today
        ).length;
        
        return {
          ...tech,
          currentOrders,
          completedToday
        };
      });
      
      setTechniciansList(updatedTechnicians);
    } catch (error) {
      console.error('Error refreshing technician data:', error);
    }
  };

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, []);

  const sortOptions = [
    // { value: 'priority', label: 'Priorität' },
    // { value: 'created', label: 'Erstellungsdatum' },
    // { value: 'sla', label: 'SLA-Frist' },
    // { value: 'dealership', label: 'Autohaus' }
  ];

  // Filter and sort orders
  const filteredOrders = availableOrders?.filter(order => {
      if (searchQuery) {
        return order?.orderNumber?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
               order?.vin?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
               order?.dealership?.toLowerCase()?.includes(searchQuery?.toLowerCase());
      }
      return true;
    })?.sort((a, b) => {
      switch (sortBy) {
        case 'priority':
          const priorityOrder = { urgent: 4, high: 3, normal: 2, low: 1 };
          return priorityOrder?.[b?.priority] - priorityOrder?.[a?.priority];
        case 'created':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'sla':
          return new Date(a.slaDeadline) - new Date(b.slaDeadline);
        case 'dealership':
          return a?.dealership?.localeCompare(b?.dealership);
        default:
          return 0;
      }
    });

  // Filter technicians
  
  const filteredTechnicians = techniciansList?.filter(tech => {
    if (filters?.status && filters?.status?.length > 0) {
      const isIncluded = filters?.status?.includes(tech?.status);
      return isIncluded;
    }
    return true;
  });
  

  const handleOrderSelect = (orderId) => {
    setSelectedOrders(prev => 
      prev?.includes(orderId) 
        ? prev?.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  const handleSelectAllOrders = () => {
    if (selectedOrders?.length === filteredOrders?.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(filteredOrders?.map(order => order?.id));
    }
  };

    const handleAssignOrder = async (order, technician) => {
    try {
      if (!user?.id) {
        alert('User not authenticated');
        return;
      }

      if (!technician?.id) {
        alert('Please select a technician');
        return;
      }

      if (!order?.id) {
        alert('Please select an order to assign');
        return;
      }


      // Update the order with the assigned technician
      const { data, error } = await orders?.update(order.id, {
        assigned_technician_id: technician.id,
        status: 'assigned',
        updated_at: new Date().toISOString()
      });

      if (error) {
        throw error;
      }
      
      // Remove the assigned order from available orders and refresh the list
      setAvailableOrders(prev => prev.filter(o => o.id !== order.id));
      
      // Also refresh technician data to update their order counts
      await refreshTechnicianData();
      
      // Return success for the TechnicianCard to handle
      return true;
      
    } catch (error) {
      console.error('Error assigning order:', error);
      alert('Error assigning order: ' + error.message);
      throw error; // Re-throw for TechnicianCard to handle
    }
  };

  const handleBulkAssignment = async (selectedOrders, technicians, mode, criteria) => {
    try {
      if (!user?.id) {
        alert('User not authenticated');
        return;
      }


      // Implement bulk assignment logic based on mode and criteria
      let successCount = 0;
      let errorCount = 0;

      for (const order of selectedOrders) {
        try {
          
          // Validate order structure
          if (!order?.id) {
            errorCount++;
            continue;
          }
          
          let technician;
          
          if (mode === 'manual' && technicians?.length > 0) {
            // Use the selected technician
            technician = technicians[0];
          } else if (mode === 'auto') {
            // Find the first available technician
            technician = technicians?.find(t => t?.status === 'available');
          }
          
          
          if (technician) {
            const { data, error } = await orders?.update(order.id, {
              assigned_technician_id: technician.id,
              status: 'assigned',
              updated_at: new Date().toISOString()
            });

            if (error) {
              console.error('Database error for order', order.id, ':', error);
              throw error;
            }
            
            successCount++;
          } else {
            console.error('No technician available for order', order.id);
            errorCount++;
          }
        } catch (error) {
          console.error('Error assigning order:', order.id, error);
          errorCount++;
        }
      }

      if (successCount > 0) {
        alert(`${successCount} Aufträge erfolgreich zugewiesen`);
      }
      if (errorCount > 0) {
        alert(`${errorCount} Aufträge konnten nicht zugewiesen werden`);
      }

    setSelectedOrders([]);
    setIsBulkModalOpen(false);
      
      // Reload data to reflect changes
      await loadData();
      
    } catch (error) {
      console.error('Error in bulk assignment:', error);
      alert('Error in bulk assignment: ' + error.message);
    }
  };

  const handleViewTechnicianDetails = (technician) => {
    setSelectedTechnician(technician);
    setIsTechnicianDetailsModalOpen(true);
  };

  // Handle technician deletion
  const handleDeleteTechnician = async (technician) => {
    // Check if technician has assigned orders
    const assignedOrders = availableOrders.filter(order => order.assigned_technician_id === technician.id);
    
    if (assignedOrders.length > 0) {
      const confirmMessage = `Der Techniker "${technician.name}" hat ${assignedOrders.length} zugewiesene Aufträge. Möchten Sie den Techniker trotzdem löschen? Die Aufträge werden automatisch freigegeben.`;
      
      if (!window.confirm(confirmMessage)) {
        return;
      }
    } else {
      if (!window.confirm(`Möchten Sie den Techniker "${technician.name}" wirklich löschen?`)) {
        return;
      }
    }

    try {
      setLoading(true);
      
      // First, unassign any orders from this technician
      if (assignedOrders.length > 0) {
        const orderIds = assignedOrders.map(order => order.id);
        const { error: unassignError } = await supabase
          .from('orders')
          .update({ assigned_technician_id: null })
          .in('id', orderIds);
        
        if (unassignError) {
          console.error('Error unassigning orders:', unassignError);
          alert('Fehler beim Freigeben der Aufträge. Der Techniker wurde nicht gelöscht.');
          return;
        }
      }

      // Delete the technician
      const { error: deleteError } = await technicians.delete(technician.id);
      
      if (deleteError) {
        console.error('Error deleting technician:', deleteError);
        alert('Fehler beim Löschen des Technikers: ' + deleteError.message);
        return;
      }

      // Reload data
      await loadData();
      
      alert(`Techniker "${technician.name}" wurde erfolgreich gelöscht.`);
      
    } catch (error) {
      console.error('Error deleting technician:', error);
      alert('Fehler beim Löschen des Technikers: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignTechnicianToOrder = (order) => {
    // Select this order and open the bulk assignment modal
    setSelectedOrders([order.id]);
    setIsBulkModalOpen(true);
  };

  const handleViewOrderDetails = (order) => {
    navigate('/order-management-dashboard', { state: { order } });
  };

  const handleOptimizeAssignments = () => {
  };

  const handleClearAssignments = () => {
    setSelectedOrders([]);
  };

  const handleAssignmentComplete = async () => {
    // Reload data to reflect changes
    await loadData();
    // Clear selected orders
    setSelectedOrders([]);
  };

  const handleTechnicianAdded = async (newTechnician) => {
    // Reload data to include the new technician
    await loadData();
  };

  const handleTechnicianUpdated = async (updatedTechnician) => {
    // Update the technician in the local state
    setTechniciansList(prev => 
      prev.map(tech => 
        tech.id === updatedTechnician.id ? updatedTechnician : tech
      )
    );
  };

  const slaMetrics = {
    onTimeRate: '94%',
    atRiskOrders: '5',
    overdueOrders: '2'
  };

  return (
    <div className="min-h-screen bg-background">
      <Header userRole="admin" />
      {/* Hide sidebar on mobile */}
      <div className="hidden lg:block">
        <Sidebar 
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          userRole="admin"
        />
      </div>
      <main className={`pt-16 transition-all duration-200 h-screen ${
        isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'
      }`}>
        <div className="w-full px-4 sm:px-6 lg:px-8 py-6 h-full flex flex-col">
          <Breadcrumb />
          
          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground mb-2">
                Techniker-Zuordnung
              </h1>
              <p className="text-text-secondary">
                Verwalten Sie Techniker-Zuweisungen und optimieren Sie die Arbeitsbelastung
              </p>
            </div>
            <div className="flex items-center space-x-3 mt-4 lg:mt-0">
              <Button
                variant="default"
                onClick={() => setIsAddTechnicianModalOpen(true)}
              >
                <Icon name="UserPlus" size={16} className="mr-2" />
                Techniker hinzufügen
              </Button>
              {selectedOrders?.length > 0 && (
                <Button
                  variant="default"
                  onClick={() => setIsBulkModalOpen(true)}
                >
                  <Icon name="Users" size={16} className="mr-2" />
                  {selectedOrders?.length} Aufträge zuweisen
                </Button>
              )}
              {/* <Button
                variant="outline"
                onClick={handleOptimizeAssignments}
              >
                <Icon name="Zap" size={16} className="mr-2" />
                Optimieren
              </Button> */}
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700">Error: {error}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={loadData}
                className="mt-2"
              >
                Retry
              </Button>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-blue-700">Loading technicians and orders...</p>
            </div>
          )}

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 h-full">
            {/* Left Panel - Technicians */}
            <div className="lg:col-span-2 h-full flex flex-col">
              <div className="space-y-4 flex-1 flex flex-col">
                {/* Filter Panel */}
                {/* <FilterPanel
                  filters={filters}
                  onFiltersChange={setFilters}
                  onReset={() => setFilters({})}
                  isExpanded={isFilterExpanded}
                  onToggleExpanded={() => setIsFilterExpanded(!isFilterExpanded)}
                  onExport={() => console.log('Export filters')}
                  isCollapsed={!isFilterExpanded}
                  onToggleCollapse={() => setIsFilterExpanded(!isFilterExpanded)}
                /> */}

                {/* Technicians List */}
                <div className="bg-card border border-border rounded-lg flex-1 flex flex-col">
                  <div className="p-4 border-b border-border">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-foreground">
                        Verfügbare Techniker ({filteredTechnicians?.length})
                      </h3>
                      <div className="flex items-center space-x-2">
                        <div className="flex items-center space-x-1">
                          {/* <div className="w-2 h-2 bg-success rounded-full"></div>
                          <span className="text-xs text-text-secondary">
                            {filteredTechnicians?.filter(t => t?.status === 'available')?.length} verfügbar
                          </span> */}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 space-y-3 flex-1 overflow-y-auto">
                                        {filteredTechnicians?.length > 0 ? (
                      filteredTechnicians?.map((technician) => (
                      <TechnicianCard
                        key={technician?.id}
                        technician={technician}
                        onAssignOrder={handleAssignOrder}
                        onViewDetails={handleViewTechnicianDetails}
                        onDeleteTechnician={handleDeleteTechnician}
                        isDragOver={draggedOrder !== null}
                          selectedOrders={selectedOrders?.map(id => filteredOrders?.find(o => o?.id === id))?.filter(Boolean)}
                          onAssignmentComplete={handleAssignmentComplete}
                        />
                      ))
                    ) : (
                      <div className="text-center py-8">
                        <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-3" />
                        <h3 className="text-lg font-medium text-foreground mb-1">
                          Keine Techniker verfügbar
                        </h3>
                        <p className="text-text-secondary">
                          Es sind derzeit keine aktiven Techniker im System registriert.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Center Panel - Unassigned Orders */}
            <div className="lg:col-span-10">
              <div className="bg-card border border-border rounded-lg h-full flex flex-col">
                {/* Header */}
                <div className="p-4 border-b border-border">
                  {/* Summary Info */}
                  {/* <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center space-x-2 text-blue-700">
                      <Icon name="Info" size={16} />
                      <span className="text-sm font-medium">
                        Zeige nur Aufträge mit Status "Neu" (pending) - zugewiesene, in Bearbeitung, erledigte und stornierte Aufträge werden nicht angezeigt
                      </span>
                    </div>
                  </div> */}
                  
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                    <div className="flex items-center space-x-3">
                      <h3 className="font-semibold text-foreground">
                        Verfügbare Aufträge für Zuweisung ({filteredOrders?.length})
                      </h3>
                      {selectedOrders?.length > 0 && (
                        <span className="bg-primary text-primary-foreground text-xs rounded-full px-2 py-1 font-medium">
                          {selectedOrders?.length} ausgewählt
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleSelectAllOrders}
                      >
                        {selectedOrders?.length === filteredOrders?.length ? 'Alle abwählen' : 'Alle auswählen'}
                      </Button>
                    </div>
                  </div>
                  
                  {/* Search and Sort */}
                  <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 mt-3">
                    <div className="flex-1">
                      <Input
                        type="search"
                        placeholder="Aufträge suchen..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e?.target?.value)}
                      />
                    </div>
                    {/* <div className="w-full sm:w-48">
                      <Select
                        placeholder="Sortieren nach..."
                        options={sortOptions}
                        value={sortBy}
                        onChange={setSortBy}
                      />
                    </div> */}
                  </div>
                </div>

                {/* Orders Table */}
                <div className="overflow-x-auto flex-1 flex flex-col">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="p-4 text-left">
                          <input
                            type="checkbox"
                            checked={selectedOrders?.length === filteredOrders?.length && filteredOrders?.length > 0}
                            onChange={handleSelectAllOrders}
                            className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary focus:ring-2"
                          />
                        </th>
                        <th className="p-4 text-left text-sm font-medium text-foreground">AUFTRAGSNR.</th>
                        <th className="p-4 text-left text-sm font-medium text-foreground">VIN</th>
                        <th className="p-4 text-left text-sm font-medium text-foreground">AUTOHAUS</th>
                        <th className="p-4 text-left text-sm font-medium text-foreground">SERVICE</th>
                        <th className="p-4 text-left text-sm font-medium text-foreground">PRIORITÄT</th>
                        <th className="p-4 text-left text-sm font-medium text-foreground">TECHNIKER</th>
                        <th className="p-4 text-left text-sm font-medium text-foreground">ERSTELLT</th>
                        <th className="p-4 text-left text-sm font-medium text-foreground">AKTIONEN</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {filteredOrders?.map((order) => (
                        <tr 
                          key={order?.id} 
                          className="hover:bg-muted/30 transition-colors cursor-pointer"
                          onClick={() => handleAssignTechnicianToOrder(order)}
                        >
                          <td className="p-4">
                            <input
                              type="checkbox"
                              checked={selectedOrders?.includes(order?.id)}
                              onChange={() => handleOrderSelect(order?.id)}
                              onClick={(e) => e.stopPropagation()}
                              className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary focus:ring-2"
                            />
                          </td>
                          <td className="p-4">
                            <div>
                              <div className="font-medium text-foreground">#{order?.orderNumber}</div>
                              <div className="text-sm text-text-secondary">{order?.customerName}</div>
                            </div>
                          </td>
                          <td className="p-4">
                            <div>
                              <div className="font-medium text-foreground">{order?.vin}</div>
                              <div className="text-sm text-text-secondary">{order?.licensePlate || 'N/A'}</div>
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="text-sm text-foreground">{order?.dealership}</div>
                          </td>
                          <td className="p-4">
                            <div>
                              <div className="text-sm font-medium text-foreground">{order?.serviceType}</div>
                              <div className="text-xs text-text-secondary">{order?.serviceDescription}</div>
                            </div>
                          </td>
                          <td className="p-4">
                            <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                              order?.priority === 'urgent' ? 'bg-error text-error-foreground' :
                              order?.priority === 'high' ? 'bg-warning text-warning-foreground' :
                              order?.priority === 'low' ? 'bg-muted text-muted-foreground' :
                              'bg-accent text-accent-foreground'
                            }`}>
                              {order?.priority === 'urgent' ? 'Dringend' :
                               order?.priority === 'high' ? 'Hoch' :
                               order?.priority === 'low' ? 'Niedrig' : 'Normal'}
                            </span>
                          </td>
                          <td className="p-4">
                            <div className="text-sm text-foreground">
                              {order?.assignedTechnician?.name || 'Nicht zugewiesen'}
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="text-sm text-text-secondary">
                              {new Date(order?.createdAt)?.toLocaleDateString('de-DE')}
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="flex space-x-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewOrderDetails(order);
                                }}
                              >
                                <Icon name="Eye" size={14} className="mr-1" />
                                Details
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  {filteredOrders?.length === 0 && (
                    <div className="text-center py-8">
                      <Icon name="ClipboardList" size={48} className="text-muted-foreground mx-auto mb-3" />
                      <h3 className="text-lg font-medium text-foreground mb-1">
                        Keine Aufträge gefunden
                      </h3>
                      <p className="text-text-secondary">
                        {searchQuery ? 'Versuchen Sie andere Suchbegriffe' : 'Alle Aufträge sind bereits zugewiesen oder es sind keine Aufträge im System vorhanden'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Panel - Assignment Summary */}
            {/* <div className="lg:col-span-1">
              <AssignmentSummary
                assignments={filteredTechnicians}
                slaMetrics={slaMetrics}
                onOptimize={handleOptimizeAssignments}
                onClearAll={handleClearAssignments}
              />
            </div> */}
          </div>
        </div>
      </main>
      {/* Bulk Assignment Modal */}
      <BulkAssignmentModal
        isOpen={isBulkModalOpen}
        onClose={() => setIsBulkModalOpen(false)}
        selectedOrders={selectedOrders?.map(id => filteredOrders?.find(o => o?.id === id))?.filter(Boolean)}
        availableTechnicians={filteredTechnicians}
        onAssign={handleBulkAssignment}
      />

      {/* Add Technician Modal */}
      <AddTechnicianModal
        isOpen={isAddTechnicianModalOpen}
        onClose={() => setIsAddTechnicianModalOpen(false)}
        onTechnicianAdded={handleTechnicianAdded}
      />

      {/* Technician Details Modal */}
      <TechnicianDetailsModal
        isOpen={isTechnicianDetailsModalOpen}
        onClose={() => {
          setIsTechnicianDetailsModalOpen(false);
          setSelectedTechnician(null);
        }}
        technician={selectedTechnician}
        onTechnicianUpdated={handleTechnicianUpdated}
      />
    </div>
  );
};

export default TechnicianAssignment;